import React, {RefObject, useEffect, useState} from 'react';
import * as ReactDOM from 'react-dom';
import * as dragDrop from '@hello-pangea/dnd';
import dragDropScript from '@hello-pangea/dnd/dist/dnd.min.js?url';
import {isPostHogEnabled, posthog} from '../../../../../utils/posthog/posthog';

type DragDrop = typeof dragDrop;
type DragDropWindow = Window & {
    React?: typeof React;
    ReactDOM?: typeof ReactDOM;
    ReactBeautifulDnd?: DragDrop;
};

const libraries = new WeakMap<Document, Promise<DragDrop>>();

function loadDragDrop(doc: Document): Promise<DragDrop> {
    const existing = libraries.get(doc);
    if (existing) return existing;

    const library = new Promise<DragDrop>((resolve, reject) => {
        const view = doc.defaultView as DragDropWindow;
        const previousReact = view.React;
        const previousReactDOM = view.ReactDOM;
        view.React = React;
        view.ReactDOM = ReactDOM;

        const script = doc.createElement('script');
        script.src = dragDropScript;
        const restore = (): void => {
            view.React = previousReact;
            view.ReactDOM = previousReactDOM;
            script.remove();
        };
        script.onload = () => {
            restore();
            if (view.ReactBeautifulDnd) resolve(view.ReactBeautifulDnd);
            else reject(new Error('Unable to initialize sequence drag and drop'));
        };
        script.onerror = () => {
            restore();
            reject(new Error('Unable to load sequence drag and drop'));
        };
        doc.head.appendChild(script);
    });
    libraries.set(doc, library);
    library.catch(() => libraries.delete(doc));
    return library;
}

export function useSequenceDragDrop(root: RefObject<HTMLElement>, hostStyled: boolean): DragDrop | null {
    const [library, setLibrary] = useState<DragDrop | null>(null);

    useEffect(() => {
        const doc = root.current?.ownerDocument;
        if (!hostStyled || !doc) return undefined;

        let active = true;
        loadDragDrop(doc)
            .then((loaded) => {
                if (active) setLibrary(loaded);
            })
            .catch((error: unknown) => {
                if (isPostHogEnabled()) posthog.captureException(error);
            });
        return () => {
            active = false;
        };
    }, [root, hostStyled]);

    return hostStyled ? library : dragDrop;
}
